import axiosInstance from '../utils/axiosInstance';

export interface TagGroup {
    id: number;
    user_id: number;
    name: string;
    display_order: number;
    created_at: string;
    updated_at: string;
}

export interface TagGroupPreference {
    tag: string;
    is_preferred: boolean;
}

export interface TagGroupWithPreferences extends TagGroup {
    preferences: TagGroupPreference[];
}

export interface TagGroupsResponse {
    tag_groups: TagGroup[];
    total: number;
}

export interface CreateTagGroupRequest {
    name: string;
    display_order?: number;
}

export interface UpdateTagGroupRequest {
    name?: string;
    display_order?: number;
}

export interface ReorderTagGroupRequest {
    display_order: number;
}

const TagGroupService = {
    /**
     * Get all tag groups for the current user
     */
    getUserTagGroups: async (): Promise<TagGroupsResponse> => {
        try {
            const response = await axiosInstance.get('/tag-groups');
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch tag groups');
        }
    },

    /**
     * Get a single tag group by ID
     */
    getTagGroupById: async (groupId: number): Promise<TagGroup> => {
        try {
            const response = await axiosInstance.get(`/tag-groups/${groupId}`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch tag group');
        }
    },

    /**
     * Get tag group with all its preferences
     */
    getTagGroupWithPreferences: async (groupId: number): Promise<TagGroupWithPreferences> => {
        try {
            const response = await axiosInstance.get(`/tag-groups/${groupId}/with-preferences`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch tag group with preferences');
        }
    },

    /**
     * Create a new tag group
     */
    createTagGroup: async (request: CreateTagGroupRequest): Promise<any> => {
        try {
            const response = await axiosInstance.post('/tag-groups', request);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to create tag group');
        }
    },

    /**
     * Update a tag group (name and/or display_order)
     */
    updateTagGroup: async (groupId: number, request: UpdateTagGroupRequest): Promise<any> => {
        try {
            const response = await axiosInstance.patch(`/tag-groups/${groupId}`, request);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to update tag group');
        }
    },

    /**
     * Reorder a tag group
     */
    reorderTagGroup: async (groupId: number, request: ReorderTagGroupRequest): Promise<any> => {
        try {
            const response = await axiosInstance.patch(`/tag-groups/${groupId}/reorder`, request);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to reorder tag group');
        }
    },

    /**
     * Delete a tag group (and its preferences via CASCADE)
     */
    deleteTagGroup: async (groupId: number): Promise<any> => {
        try {
            const response = await axiosInstance.delete(`/tag-groups/${groupId}`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to delete tag group');
        }
    },

    /**
     * Check if user can create more tag groups
     */
    canCreateMoreGroups: async (): Promise<{ canCreate: boolean; currentCount: number; maxAllowed: number }> => {
        try {
            const response = await axiosInstance.get('/tag-groups/can-create/check');
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to check group creation limit');
        }
    },

    /**
     * Get personalized news for a specific tag group
     */
    getPersonalizedNewsByGroup: async (groupId: number, page: number, pageSize: number): Promise<any> => {
        try {
            const response = await axiosInstance.get(`/user-preferences/news/group/${groupId}`, {
                params: { page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch personalized news by group');
        }
    },
};

export default TagGroupService;
